import { db } from '../firebase';
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  collection,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  updateDoc,
} from 'firebase/firestore';

// Encode email to a valid Firestore document ID
function encodeEmail(email) {
  return email.toLowerCase().replace(/@/g, '_at_').replace(/\./g, '_dot_');
}

// ─── User email registry ───────────────────────────────────────────────────────

/**
 * Register the signed-in user's email so others can look them up.
 * Called on every login; safe to call repeatedly (idempotent upsert).
 */
export async function registerUserEmail(uid, email, displayName) {
  if (!uid || !email) return;
  try {
    await setDoc(
      doc(db, 'usersByEmail', encodeEmail(email)),
      { uid, email, displayName: displayName || '' },
      { merge: true }
    );
  } catch (err) {
    console.warn('registerUserEmail failed:', err.message);
  }
}

/**
 * Look up a user by email address.
 * Returns { uid, email, displayName } or null if not found.
 */
export async function lookupUserByEmail(email) {
  if (!email) return null;
  try {
    const snap = await getDoc(doc(db, 'usersByEmail', encodeEmail(email)));
    if (!snap.exists()) return null;
    return snap.data();
  } catch (err) {
    if (err.code === 'permission-denied') throw err; // surface rules issues to the caller
    console.error('lookupUserByEmail failed:', err.message);
    return null;
  }
}

// ─── Firestore path helpers ───────────────────────────────────────────────────

function expenseFileRef(uid, filename) {
  return doc(db, 'users', uid, 'expenseFiles', filename);
}

function sharedReportRef(uid, filename) {
  return doc(db, 'users', uid, 'sharedReports', filename);
}

function receivedReportRef(granteeUid, ownerUid, filename) {
  return doc(db, 'users', granteeUid, 'receivedReports', `${ownerUid}_${filename}`);
}

function receivedReportsCollection(uid) {
  return collection(db, 'users', uid, 'receivedReports');
}

// ─── Owner sharing controls ───────────────────────────────────────────────────

export async function markReportShared(uid, filename) {
  const path = `users/${uid}/expenseFiles/${filename}`;
  await Promise.all([
    updateDoc(expenseFileRef(uid, filename), { isShared: true, sharedWith: [], editableBy: [] }),
    setDoc(sharedReportRef(uid, filename), {
      path,
      filename,
      grantees: [],
      createdAt: serverTimestamp(),
    }),
  ]);
}

export async function markReportUnshared(uid, filename) {
  // 1. Read current grantees
  const sharedSnap = await getDoc(sharedReportRef(uid, filename));
  const grantees = sharedSnap.exists() ? (sharedSnap.data().grantees ?? []) : [];

  // 2. Delete each grantee's receivedReports entry
  await Promise.all(
    grantees.map(({ uid: granteeUid }) =>
      deleteDoc(receivedReportRef(granteeUid, uid, filename)).catch(() => {})
    )
  );

  // 3. Clear all sharing fields on the expense file
  await updateDoc(expenseFileRef(uid, filename), { isShared: false, sharedWith: [], editableBy: [] });

  // 4. Delete the sharedReports metadata doc
  await deleteDoc(sharedReportRef(uid, filename));
}

/**
 * Grant a user access to a shared report.
 * access: 'view' | 'edit' (default 'edit')
 * - sharedWith gates Firestore reads for all grantees
 * - editableBy gates Firestore writes for edit-access grantees only
 */
export async function grantAccess(ownerUid, ownerEmail, filename, granteeUid, granteeEmail, access = 'edit') {
  const path = `users/${ownerUid}/expenseFiles/${filename}`;
  const fileUpdate = { sharedWith: arrayUnion(granteeUid) };
  if (access === 'edit') fileUpdate.editableBy = arrayUnion(granteeUid);

  await Promise.all([
    updateDoc(expenseFileRef(ownerUid, filename), fileUpdate),
    updateDoc(sharedReportRef(ownerUid, filename), {
      grantees: arrayUnion({ uid: granteeUid, email: granteeEmail, access }),
    }),
    setDoc(receivedReportRef(granteeUid, ownerUid, filename), {
      path,
      ownerUid,
      ownerEmail,
      filename,
      access,
      grantedAt: serverTimestamp(),
    }),
  ]);
}

/**
 * Revoke a grantee's access entirely.
 * Reads the current grantees list to reconstruct the exact stored object
 * (avoiding arrayRemove deep-equality mismatches on schema changes).
 */
export async function revokeAccess(ownerUid, filename, granteeUid) {
  const snap = await getDoc(sharedReportRef(ownerUid, filename));
  const grantees = snap.exists() ? (snap.data().grantees ?? []) : [];

  await Promise.all([
    updateDoc(expenseFileRef(ownerUid, filename), {
      sharedWith: arrayRemove(granteeUid),
      editableBy: arrayRemove(granteeUid),
    }),
    updateDoc(sharedReportRef(ownerUid, filename), {
      grantees: grantees.filter(g => g.uid !== granteeUid),
    }),
    deleteDoc(receivedReportRef(granteeUid, ownerUid, filename)),
  ]);
}

/**
 * Change a grantee's access level between 'view' and 'edit'.
 */
export async function changeAccess(ownerUid, filename, granteeUid, newAccess) {
  const snap = await getDoc(sharedReportRef(ownerUid, filename));
  const grantees = snap.exists() ? (snap.data().grantees ?? []) : [];
  const updatedGrantees = grantees.map(g =>
    g.uid === granteeUid ? { ...g, access: newAccess } : g
  );

  await Promise.all([
    updateDoc(expenseFileRef(ownerUid, filename), {
      ...(newAccess === 'edit'
        ? { editableBy: arrayUnion(granteeUid) }
        : { editableBy: arrayRemove(granteeUid) }),
    }),
    updateDoc(sharedReportRef(ownerUid, filename), { grantees: updatedGrantees }),
    updateDoc(receivedReportRef(granteeUid, ownerUid, filename), { access: newAccess }),
  ]);
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export async function getSharedReportDoc(uid, filename) {
  try {
    const snap = await getDoc(sharedReportRef(uid, filename));
    if (!snap.exists()) return null;
    return snap.data();
  } catch (err) {
    console.error('getSharedReportDoc failed:', err.message);
    return null;
  }
}

/**
 * List all reports shared with the given uid (i.e. reports owned by others).
 * Returns array of { path, ownerUid, ownerEmail, filename, access, grantedAt }.
 */
export async function listReceivedReports(uid) {
  try {
    const snap = await getDocs(receivedReportsCollection(uid));
    return snap.docs.map((d) => d.data());
  } catch (err) {
    console.error('listReceivedReports failed:', err.message);
    return [];
  }
}
